import { ShiftStatus, WaypointStatusName, WaypointActionStatusName, ProcedureStatus, SampleStatus } from '@caresend/types';
import { hasStatus, nullishFilter, deduplicateArray, objectFilter, objectMap, generateID, formatFullName, formatPhoneNumber, initStatus, formatCamelCase } from '@caresend/utils';
import { defineComponent, computed, ref, watchEffect } from 'vue';
import __vue_component__$1 from '../../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__ from '../../../../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$5 from '../../../../../inputs/SwitchComponent.vue.js';
import __vue_component__$8 from '../../ItineraryList/components/ExpandCopyID.vue.js';
import __vue_component__$9 from '../../ItineraryList/components/ItineraryCard/components/ItineraryDraftWaypointSupplyStatuses.vue.js';
import __vue_component__$4 from '../../ItineraryList/components/ItineraryRelatedStatusView.vue.js';
import { useListController } from '../../Portal/components/useListController.js';
import { usePortalStore } from '../../../store/portal/index.js';
import __vue_component__$2 from '../../../../../ui/Icon.vue.js';
import __vue_component__$3 from '../../../../../ui/InteractivePopup/InteractivePopup.vue.js';
import __vue_component__$6 from '../../../../../ui/Tooltip/Tooltip.vue.js';
import __vue_component__$7 from '../../../../../ui/UserPhoto/UserPhoto.vue.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import { copyToClipboard } from '../../../../../../functions/clipboard.js';
import '../../../../../../functions/configuration/icons.js';
import '../../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../../functions/tracking/index.js';
import { getStore } from '../../../../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'ItineraryWaypointPopover',
    components: {
        BasicCard: __vue_component__,
        ButtonComponent: __vue_component__$1,
        Icon: __vue_component__$2,
        InteractivePopup: __vue_component__$3,
        ItineraryRelatedStatusView: __vue_component__$4,
        SwitchComponent: __vue_component__$5,
        Tooltip: __vue_component__$6,
        UserPhoto: __vue_component__$7,
        ExpandCopyID: __vue_component__$8,
        ItineraryDraftWaypointSupplyStatuses: __vue_component__$9,
    },
    props: {
        inputDisabled: {
            type: Boolean,
        },
        waypointInItinerary: {
            type: Boolean,
            default: () => false,
        },
        waypoint: {
            type: Object,
            required: true,
        },
        waypointLetter: {
            type: String,
            required: true,
        },
        waypointLetterClassExtension: {
            type: String,
            default: undefined,
        },
        justify: {
            type: String,
            default: 'start',
        },
    },
    emits: ['openWaypointDetails', 'deleteWaypoint'],
    setup(props) {
        const portalStore = usePortalStore();
        const { viewAs } = portalStore;
        const store = getStore();
        const { setWaypointIndex } = useListController();
        const baseClassName = 'ItineraryWaypointPopover__letter-container';
        const waypointLetterClass = computed(() => props.waypointLetterClassExtension
            ? [baseClassName, `${baseClassName}${props.waypointLetterClassExtension}`]
            : [baseClassName]);
        const isShiftCheckedIn = computed(() => {
            const { shiftID } = portalStore.itinerary.value;
            if (!shiftID)
                return false;
            const shift = portalStore.shifts.getRawClone(shiftID);
            return hasStatus(shift, [
                ShiftStatus.IN_PROGRESS,
                ShiftStatus.COMPLETE,
                ShiftStatus.FAILED,
            ]);
        });
        const updateStatus = (status) => {
            if (!props.waypoint)
                return;
            portalStore.waypoints.setChildOf(props.waypoint.id, 'status', initStatus(status), 'update waypoint status');
        };
        const getWaypointActionPreviewByID = (wpaID) => { var _a; return (_a = portalStore.waypointActions.get(wpaID)) !== null && _a !== void 0 ? _a : undefined; };
        const waypointActions = computed(() => {
            var _a, _b;
            if (props.waypoint) {
                return (_b = (_a = portalStore.waypoints.get(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.computed.waypointActions) !== null && _b !== void 0 ? _b : [];
            }
            return [];
        });
        const getWaypointActionProcedureIDs = (wpaID) => {
            var _a, _b;
            if (!props.waypointInItinerary) {
                const wpa = getWaypointActionPreviewByID(wpaID);
                return Object.keys((_a = wpa === null || wpa === void 0 ? void 0 : wpa.procedures) !== null && _a !== void 0 ? _a : {});
            }
            const waypointAction = portalStore.waypointActions.get(wpaID);
            return Object.keys((_b = waypointAction === null || waypointAction === void 0 ? void 0 : waypointAction.procedures) !== null && _b !== void 0 ? _b : {});
        };
        const allProcedures = computed(() => {
            const procedureIDs = waypointActions.value.map((wpa) => getWaypointActionProcedureIDs(wpa.id)).flat();
            return procedureIDs.map((procedureID) => {
                var _a;
                return (_a = portalStore.procedures.get(procedureID)) !== null && _a !== void 0 ? _a : store.getters['procedures/getProcedureByID'](procedureID);
            }).filter(nullishFilter);
        });
        const sampleScanning = ref(undefined);
        const sampleProcessing = ref(undefined);
        watchEffect(() => {
            sampleScanning.value = allProcedures.value
                .some((procedure) => !(procedure === null || procedure === void 0 ? void 0 : procedure.disableSampleScanning));
            sampleProcessing.value = allProcedures.value
                .some((procedure) => !(procedure === null || procedure === void 0 ? void 0 : procedure.disableSampleProcessing));
        });
        const setSampleScanning = (value) => {
            sampleScanning.value = value;
            allProcedures.value.forEach((procedure) => {
                const key = 'disableSampleScanning';
                portalStore.procedures.setChildOf(procedure.id, key, !value, `set procedure ${formatCamelCase(key)}`);
            });
        };
        const setSampleProcessing = (value) => {
            sampleProcessing.value = value;
            allProcedures.value.forEach((procedure) => {
                const key = 'disableSampleProcessing';
                portalStore.procedures.setChildOf(procedure.id, key, !value, `set procedure ${formatCamelCase(key)}`);
            });
        };
        const schedulingUserIDs = computed(() => deduplicateArray(allProcedures.value.map((p) => p.orderID)
            .map((orderID) => store.state.orders.orders[orderID])
            .filter(nullishFilter)
            .map((order) => order.schedulingUserID)
            .filter(nullishFilter)));
        const procedureContacts = computed(() => objectFilter(Object.fromEntries(allProcedures.value.map((p) => {
            var _a, _b, _c, _d;
            return [
                (_d = (_b = (_a = p.contact) === null || _a === void 0 ? void 0 : _a.userID) !== null && _b !== void 0 ? _b : (_c = p.contact) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : p.id,
                p.contact,
            ];
        })), nullishFilter));
        const contacts = computed(() => objectMap(procedureContacts.value, (contact) => {
            var _a, _b, _c, _d, _e;
            const firstName = (_b = (_a = contact.name) === null || _a === void 0 ? void 0 : _a.split(' ')[0]) !== null && _b !== void 0 ? _b : '';
            const lastName = (_d = (_c = contact.name) === null || _c === void 0 ? void 0 : _c.split(' ').slice(1).join(' ')) !== null && _d !== void 0 ? _d : '';
            const id = (_e = contact.userID) !== null && _e !== void 0 ? _e : generateID();
            let info = {
                firstName,
                lastName,
                phone: contact.phone,
            };
            if (contact.userID) {
                const user = store.state.users.users[contact.userID];
                if (user)
                    info = user.info;
            }
            return { id, info };
        }));
        const schedulingUserInfos = computed(() => schedulingUserIDs.value
            .map((userID) => store.state.users.users[userID])
            .filter(nullishFilter)
            .map((user) => ({ id: user.id, info: user.info })));
        const edittingWpLetter = ref(props.waypointLetter);
        const onWaypointLetterInput = (payload) => {
            const target = payload.target;
            const { value } = target;
            if (typeof value === 'string') {
                /** Strip of all numbers and special characters */
                const regex = /[^a-zA-Z]/g;
                edittingWpLetter.value = value.replace(regex, '').toUpperCase();
                target.value = edittingWpLetter.value;
            }
        };
        const letterToIndex = (letter) => {
            let index = 0;
            for (let i = 0; i < letter.length; i += 1) {
                index *= 26;
                index += letter.charCodeAt(i) - 'A'.charCodeAt(0) + 1;
            }
            return index - 1;
        };
        const edittingWpIndex = computed(() => letterToIndex(edittingWpLetter.value));
        const waypointStatusPopupRef = ref(null);
        const onChangeWaypointLetter = (e) => {
            var _a;
            const target = e.target;
            if (e instanceof FocusEvent && !target.value) {
                edittingWpLetter.value = props.waypointLetter;
                target.value = props.waypointLetter;
            }
            else {
                edittingWpLetter.value = target.value;
                setWaypointIndex(props.waypoint.id, edittingWpIndex.value);
            }
            target.blur();
            (_a = waypointStatusPopupRef.value) === null || _a === void 0 ? void 0 : _a.closePopup();
        };
        const copyPhoneNumber = (phone) => {
            var _a;
            copyToClipboard((_a = phone === null || phone === void 0 ? void 0 : phone.number) !== null && _a !== void 0 ? _a : '', 'Phone number copied to clipboard');
        };
        const maxWaypointLettersLength = computed(() => portalStore.waypoints.length() > 26 ? 2 : 1);
        watchEffect(() => {
            edittingWpLetter.value = props.waypointLetter;
        });
        const resetWorkflows = () => {
            portalStore.waypoints.setChildOf(props.waypoint.id, 'status', initStatus(WaypointStatusName.IN_ITINERARY), true);
            portalStore.waypoints.setChildOf(props.waypoint.id, 'workflows', undefined, true);
            waypointActions.value.forEach((wpa) => {
                var _a;
                portalStore.waypointActions.setChildOf(wpa.id, 'status', initStatus(WaypointActionStatusName.INCOMPLETE), true);
                portalStore.waypointActions.setChildOf(wpa.id, 'workflows', undefined, true);
                Object.keys((_a = wpa.procedures) !== null && _a !== void 0 ? _a : {}).forEach((procedureID) => {
                    var _a;
                    portalStore.procedures.setChildOf(procedureID, 'status', ProcedureStatus.CREATED, true);
                    portalStore.procedures.setChildOf(procedureID, `workflows/${wpa.id}`, undefined, true);
                    const samples = (_a = portalStore.procedures.get(procedureID)) === null || _a === void 0 ? void 0 : _a.computed.extendedSamples;
                    if (samples) {
                        Object.values(samples).forEach((sample) => {
                            portalStore.procedures.setChildOf(procedureID, `samples/${sample.id}/status`, SampleStatus.PENDING, true);
                        });
                    }
                });
            });
        };
        const debugMode = computed(() => !!store.state.variables.featureFlags.debug);
        return {
            allProcedures,
            contacts,
            copyPhoneNumber,
            debugMode,
            edittingWpLetter,
            formatFullName,
            formatPhoneNumber,
            isShiftCheckedIn,
            maxWaypointLettersLength,
            onChangeWaypointLetter,
            onWaypointLetterInput,
            resetWorkflows,
            sampleProcessing,
            sampleScanning,
            schedulingUserInfos,
            setSampleProcessing,
            setSampleScanning,
            updateStatus,
            viewAs,
            waypointLetterClass,
            waypointStatusPopupRef,
        };
    },
});

export { script as default };
